import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StorefrontAPI from 'utilities/storefrontApi';
import { mergeOnSignIn } from 'utilities/storefrontCartSync';
import { saveSession } from 'utilities/storefrontSession';
import {
  wrap, cardStyle, inputStyle, primaryBtn,
  navigateToAccount, navigateToLogin,
} from './styles';
import SocialButtons from './SocialButtons';
import useBrandFromParams from './useBrandFromParams';
import BrandHeader from './BrandHeader';
import Centered from './Centered';

/**
 * Customer self-signup for a brand storefront. Only renders when the
 * brand has both `customer_login_enabled` and `customer_self_signup_enabled`
 * — brand owners can disable signups (e.g. to make the storefront
 * invite-only) without disabling login.
 */
export default function StorefrontSignup() {
  const { brand, error: brandError } = useBrandFromParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ display_name: '', email: '', password: '', marketing_email_opt_in: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const accentColor = brand?.brand_color || '#422AFB';

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!brand) return;
    setError('');
    setSubmitting(true);
    try {
      const out = await StorefrontAPI.signup(brand._id, form);
      saveSession(brand._id, { token: out.token, customer: out.customer });
      // Somebody who filled a basket and then made an account keeps it.
      await mergeOnSignIn(brand._id);
      navigateToAccount(navigate, brand);
    } catch (err) {
      setError(err?.message || 'Sign-up failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (brandError) return <Centered>{brandError}</Centered>;
  if (!brand) return <Centered>Loading…</Centered>;
  if (!brand.customer_login_enabled || !brand.customer_self_signup_enabled) {
    return <Centered>Sign-ups aren't open on this brand.</Centered>;
  }

  return (
    <div style={wrap}>
      <form style={cardStyle} onSubmit={submit}>
        <BrandHeader brand={brand} />
        <h2 style={{ margin: 0, marginBottom: 18, fontSize: 22, color: '#0f172a' }}>Create your account</h2>
        <input
          type='text' placeholder='Display name' value={form.display_name}
          onChange={set('display_name')} style={inputStyle}
        />
        <input
          type='email' placeholder='Email' value={form.email}
          onChange={set('email')} style={inputStyle}
          autoComplete='email' required
        />
        <input
          type='password' placeholder='Password' value={form.password}
          onChange={set('password')} style={inputStyle}
          autoComplete='new-password' required minLength={6}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#475569', margin: '4px 0 14px' }}>
          <input type='checkbox' checked={form.marketing_email_opt_in} onChange={set('marketing_email_opt_in')} />
          Email me updates and offers from {brand.name}.
        </label>
        {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{error}</div>}
        <button type='submit' style={primaryBtn(accentColor)} disabled={submitting}>
          {submitting ? 'Creating…' : 'Create account'}
        </button>

        <SocialButtons brandId={brand._id} returnTo='/account' />
        <div style={{ textAlign: 'center', fontSize: 13, color: '#64748b', marginTop: 14 }}>
          Already have one?{' '}
          <button
            type='button'
            style={{
              color: accentColor, fontWeight: 600,
              background: 'none', border: 'none', padding: 0,
              cursor: 'pointer', textDecoration: 'underline',
            }}
            onClick={() => navigateToLogin(navigate, brand)}
          >
            Sign in
          </button>
        </div>
      </form>
    </div>
  );
}
